import { Checkbox } from '@/components/ui/checkbox';
import { TagIndicator } from '@/components/widget/tag-list';
import { useToggleStar } from '@/hooks';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/dateUtils';
import type { ContentConversationListItem, SearchMatch } from '@vivport/backend/types';
import { MessageSquare, Star } from 'lucide-react';
import { ObligationBadge } from '@/components/widget/obligation-badge';
import { useEffect, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import { buildConversationMeta, resolveRowTagIds } from './ExploreEntryHelpers';
import { ExploreMatchLine } from './ExploreMatchLine';

interface ExploreConversationEntryProps {
  conversation: ContentConversationListItem;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  onClick?: () => void;
  match?: SearchMatch;
  meEmails?: string[];
}

export function ExploreConversationEntry({
  conversation,
  checked,
  onCheckedChange,
  onClick,
  match,
  meEmails = [],
}: ExploreConversationEntryProps) {
  const representativeMessage = conversation.representativeMessage;
  const obligationIcon = (
    <ObligationBadge contentId={representativeMessage.id} rollup={representativeMessage.obligationRollup} reserveSpace />
  );
  const isUnread = representativeMessage.tracking?.isRead !== true;
  const toggleStarMutation = useToggleStar();
  const [isStarred, setIsStarred] = useState(representativeMessage.tracking?.isStarred || false);
  const meta = useMemo(() => buildConversationMeta(conversation, meEmails), [conversation, meEmails]);
  const tagIds = resolveRowTagIds(representativeMessage);
  const latestDateLabel = useMemo(() => {
    const latestDate = new Date(meta.latestTimelineAt || representativeMessage.createdAt);
    if (Number.isNaN(latestDate.getTime())) {
      return '--';
    }
    return formatDate(latestDate);
  }, [meta.latestTimelineAt, representativeMessage.createdAt]);

  useEffect(() => {
    setIsStarred(representativeMessage.tracking?.isStarred || false);
  }, [representativeMessage.tracking?.isStarred]);

  const handleStarredChange = (nextValue: boolean) => {
    setIsStarred(nextValue);
    toggleStarMutation.mutate({ contentId: representativeMessage.id, isStarred: nextValue });
  };

  const handleMainClick = (event: React.MouseEvent) => {
    event.preventDefault();
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="checkbox"]') || target.closest('a')) {
      return;
    }
    onClick?.();
  };

  return (
    <li
      data-testid="explore-entry"
      data-content-type="CONVERSATION"
      className={cn(
        'search-result-item w-full overflow-hidden px-4 transition-colors',
        isUnread ? 'bg-background hover:bg-muted/50' : 'bg-muted/50 hover:bg-muted'
      )}
    >
      <div className="sr-only">
        <span data-testid="explore-entry-content-type">CONVERSATION</span>
        <span data-testid="explore-entry-title">{meta.subject}</span>
        <span data-testid="explore-entry-subtitle">{meta.subtitle ?? ''}</span>
      </div>

      <div className="flex py-3 sm:hidden">
        <div className="flex items-center pr-3">
          <Checkbox checked={checked} onClick={(event) => event.stopPropagation()} onCheckedChange={onCheckedChange} />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                className="focus:outline-none"
                onClick={(event) => {
                  event.stopPropagation();
                  handleStarredChange(!isStarred);
                }}
              >
                <Star
                  className={`h-4 w-4 ${isStarred ? 'text-highlight-strong fill-highlight-strong' : 'text-muted-foreground'} transition-colors`}
                />
              </button>
              <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              {obligationIcon}
              <TagIndicator tagIds={tagIds} />
            </div>
            <time className="text-sm text-muted-foreground whitespace-nowrap cursor-pointer" onClick={handleMainClick}>
              {latestDateLabel}
            </time>
          </div>

          <div className="cursor-pointer" onClick={handleMainClick}>
            <p
              className={cn(
                'flex items-baseline gap-1 text-xs',
                isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
              )}
            >
              <span className="min-w-0 truncate">{meta.partyNames}</span>
              <span className="shrink-0">{meta.partyCountLabel}</span>
            </p>
          </div>

          <div className="cursor-pointer" onClick={handleMainClick}>
            <h3
              className={cn(
                'text-sm truncate',
                isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
              )}
            >
              {meta.subject}
            </h3>
          </div>

          {meta.snippet ? (
            <div className="cursor-pointer" onClick={handleMainClick}>
              <div className="text-sm text-muted-foreground truncate">
                <Markdown
                  className="line-clamp-1"
                  components={{
                    a: ({ children }) => <span>{children}</span>
                  }}
                >
                  {meta.snippet}
                </Markdown>
              </div>
            </div>
          ) : null}
          {match ? (
            <div className="cursor-pointer" onClick={handleMainClick}>
              <ExploreMatchLine match={match} />
            </div>
          ) : null}
        </div>
      </div>

      <div className="hidden min-w-0 sm:flex items-center space-x-4">
        <div className="flex items-center space-x-2 py-3">
          <Checkbox checked={checked} onClick={(event) => event.stopPropagation()} onCheckedChange={onCheckedChange} />
          <button
            className="focus:outline-none"
            onClick={(event) => {
              event.stopPropagation();
              handleStarredChange(!isStarred);
            }}
          >
            <Star
              className={`h-4 w-4 ${isStarred ? 'text-highlight-strong fill-highlight-strong' : 'text-muted-foreground'} transition-colors`}
            />
          </button>
          <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          {obligationIcon}
          <TagIndicator tagIds={tagIds} />
        </div>

        <div className="flex w-44 min-w-0 shrink-0 items-baseline gap-1 cursor-pointer py-3" onClick={handleMainClick}>
          <span
            className={cn(
              'min-w-0 truncate text-sm',
              isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
            )}
          >
            {meta.partyNames}
          </span>
          <span
            className={cn(
              'shrink-0 text-sm',
              isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
            )}
          >
            {meta.partyCountLabel}
          </span>
        </div>

        <div className="flex-1 min-w-0 space-y-1 sm:space-y-0 cursor-pointer py-3" onClick={handleMainClick}>
          <div className="flex min-w-0 flex-col sm:flex-row sm:items-center">
            <div className="flex min-w-0 items-center gap-2 flex-wrap">
              <h3
                className={cn(
                  'min-w-0 text-sm truncate',
                  isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
                )}
              >
                {meta.subject}
              </h3>
            </div>
            <div className="min-w-0 text-sm text-muted-foreground truncate sm:ml-2 sm:flex-1 flex items-center">
              {meta.snippet ? (
                <div className="flex min-w-0 items-center">
                  <p className="hidden sm:inline"> -&nbsp;</p>
                  <Markdown
                    className="line-clamp-1 min-w-0 break-words"
                    components={{
                      a: ({ children }) => <span>{children}</span>
                    }}
                  >
                    {meta.snippet}
                  </Markdown>
                </div>
              ) : null}
            </div>
          </div>
          {match ? <ExploreMatchLine match={match} /> : null}
        </div>

        <div className="flex items-center gap-2 sm:flex-row-reverse flex-col cursor-pointer py-3" onClick={handleMainClick}>
          <time className="text-sm text-muted-foreground whitespace-nowrap">{latestDateLabel}</time>
        </div>
      </div>
    </li>
  );
}
